import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Package, Clock, CheckCircle, Truck, Eye, Trash2, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: <Clock size={14} /> },
  confirmed: { label: "Confirmed", color: "bg-blue-100 text-blue-800", icon: <CheckCircle size={14} /> },
  preparing: { label: "Preparing", color: "bg-purple-100 text-purple-800", icon: <Package size={14} /> },
  shipped: { label: "Shipped", color: "bg-indigo-100 text-indigo-800", icon: <Truck size={14} /> },
  delivered: { label: "Delivered", color: "bg-green-100 text-green-800", icon: <CheckCircle size={14} /> }
};

const Admin = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (e) {
      console.error("Error fetching orders:", e);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/orders/${orderId}`, { status: newStatus });
      toast.success(`Order status updated to ${statusConfig[newStatus].label}`);
      fetchOrders();
    } catch (e) {
      console.error("Error updating order:", e);
      toast.error("Failed to update order status");
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success("Order deleted successfully");
      fetchOrders();
    } catch (e) {
      console.error("Error deleting order:", e);
      toast.error("Failed to delete order");
    }
  };

  const filteredOrders = statusFilter === "all" 
    ? orders 
    : orders.filter(order => order.status === statusFilter);

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === "pending").length,
    confirmed: orders.filter(o => o.status === "confirmed").length,
    delivered: orders.filter(o => o.status === "delivered").length
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen py-12 bg-brand-background" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl text-brand-dark mb-2">Admin Dashboard</h1>
          <p className="font-body text-brand-muted">Manage orders and track deliveries</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <p className="font-body text-sm text-brand-muted mb-1">Total Orders</p>
              <p className="font-heading text-3xl text-brand-dark">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <p className="font-body text-sm text-brand-muted mb-1">Pending</p>
              <p className="font-heading text-3xl text-yellow-600">{stats.pending}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <p className="font-body text-sm text-brand-muted mb-1">Confirmed</p>
              <p className="font-heading text-3xl text-brand-primary">{stats.confirmed}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <p className="font-body text-sm text-brand-muted mb-1">Delivered</p>
              <p className="font-heading text-3xl text-green-600">{stats.delivered}</p>
            </CardContent>
          </Card>
        </div>

        {/* Orders Table */}
        <Card className="bg-white border-0 shadow-sm" data-testid="orders-table-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-xl">Orders</CardTitle>
            <div className="flex items-center gap-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                onClick={fetchOrders}
                className="p-2 rounded-lg bg-brand-secondary/20 hover:bg-brand-secondary/40 transition-colors"
                data-testid="refresh-orders-button"
              >
                <RefreshCw size={18} className="text-brand-primary" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 text-center">
                <p className="font-body text-brand-muted">Loading orders...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="py-12 text-center" data-testid="no-orders">
                <Package size={48} className="mx-auto text-brand-muted/40 mb-4" />
                <p className="font-body text-brand-muted">No orders found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="orders-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-body font-medium">Order Date</TableHead>
                      <TableHead className="font-body font-medium">Parent</TableHead>
                      <TableHead className="font-body font-medium">Student</TableHead>
                      <TableHead className="font-body font-medium">School</TableHead>
                      <TableHead className="font-body font-medium">Package</TableHead>
                      <TableHead className="font-body font-medium">Status</TableHead>
                      <TableHead className="font-body font-medium">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                        <TableCell className="font-body text-sm">
                          {formatDate(order.created_at)}
                        </TableCell>
                        <TableCell className="font-body text-sm">
                          <div>
                            <p className="font-medium">{order.parent_name}</p>
                            <p className="text-brand-muted text-xs">{order.parent_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-body text-sm">{order.student_name}</TableCell>
                        <TableCell className="font-body text-sm text-brand-muted">
                          {order.student_school}
                        </TableCell>
                        <TableCell className="font-body text-sm">{order.selected_package_name}</TableCell>
                        <TableCell>
                          <Select 
                            value={order.status} 
                            onValueChange={(value) => updateOrderStatus(order.id, value)}
                          >
                            <SelectTrigger className="w-32 h-8" data-testid={`status-select-${order.id}`}>
                              <Badge className={`${statusConfig[order.status]?.color} flex items-center gap-1`}>
                                {statusConfig[order.status]?.icon}
                                {statusConfig[order.status]?.label}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(statusConfig).map(([key, config]) => (
                                <SelectItem key={key} value={key}>
                                  <div className="flex items-center gap-2">
                                    {config.icon}
                                    {config.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <button
                                  onClick={() => setSelectedOrder(order)}
                                  className="p-2 rounded-lg hover:bg-brand-secondary/20 transition-colors"
                                  data-testid={`view-order-${order.id}`}
                                >
                                  <Eye size={16} className="text-brand-primary" />
                                </button>
                              </DialogTrigger>
                              <DialogContent className="max-w-lg">
                                <DialogHeader>
                                  <DialogTitle className="font-heading">Order Details</DialogTitle>
                                </DialogHeader>
                                {selectedOrder && (
                                  <div className="space-y-4 font-body text-sm">
                                    <div className="bg-brand-background rounded-lg p-4">
                                      <h4 className="font-medium text-brand-dark mb-2">Parent Info</h4>
                                      <p><span className="text-brand-muted">Name:</span> {selectedOrder.parent_name}</p>
                                      <p><span className="text-brand-muted">Email:</span> {selectedOrder.parent_email}</p>
                                      <p><span className="text-brand-muted">Phone:</span> {selectedOrder.parent_phone}</p>
                                    </div>
                                    <div className="bg-brand-background rounded-lg p-4">
                                      <h4 className="font-medium text-brand-dark mb-2">Student Info</h4>
                                      <p><span className="text-brand-muted">Name:</span> {selectedOrder.student_name}</p>
                                      <p><span className="text-brand-muted">School:</span> {selectedOrder.student_school}</p>
                                      <p><span className="text-brand-muted">Address:</span> {selectedOrder.delivery_address}</p>
                                      {selectedOrder.dorm_info && (
                                        <p><span className="text-brand-muted">Dorm:</span> {selectedOrder.dorm_info}</p>
                                      )}
                                    </div>
                                    <div className="bg-brand-secondary/20 rounded-lg p-4">
                                      <h4 className="font-medium text-brand-dark mb-2">Package</h4>
                                      <p className="font-medium text-brand-primary">{selectedOrder.selected_package_name}</p>
                                      {selectedOrder.custom_message && (
                                        <div className="mt-2">
                                          <p className="text-brand-muted text-xs mb-1">Custom Message:</p>
                                          <p className="font-accent italic text-brand-dark">"{selectedOrder.custom_message}"</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                            <button
                              onClick={() => deleteOrder(order.id)}
                              className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                              data-testid={`delete-order-${order.id}`}
                            >
                              <Trash2 size={16} className="text-red-500" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Admin;
